/**
 * placeAnchorCommand — "Place anchor here" on the viewport right-click menu
 * (ENG-590).
 *
 * A named anchor is a world position the character LLM can address by name
 * ("walk to the door"), stored on the environment row. The only way to know
 * where the door IS, is to look at the environment and point at it — so the
 * authoring gesture is a right-click on the floor, and the registry
 * (viewportCommands.js) is how a feature contributes one without core naming
 * it. The name is typed in the row's flyout editor (PlaceAnchorEditor), the
 * same shape walk-here's settings use.
 *
 * ── Which environment it writes to ──────────────────────────────────────────
 * The one you are LOOKING at, resolved from the live splat URL through
 * environmentFiles' url -> asset id pairing. Never the content browser's
 * selection: the two can differ, the click happened in the viewport, and an
 * anchor written onto an environment the user is not looking at is a position
 * that means nothing on the row that got it.
 *
 * ── Why it saves immediately ────────────────────────────────────────────────
 * A right-click has no Save button in front of it. Leaving the placement dirty
 * until someone happens to open the inspector loses it on the next reload with
 * nothing on screen having said so. The inspector's edits stay explicit (they
 * have a Save button); this one does not.
 *
 * The pure half — the name rules, the map edits — is environmentAnchors.js.
 * `resolve` here is pure enough to unit-test through injected collaborators.
 */

import { registerViewportCommand } from './viewportCommands.js'
import { environmentAnchorStore } from './environmentAnchorStore.js'
import { anchorFromPoint, labelError, suggestName } from './environmentAnchors.js'
import { knownAssetId } from './environmentFiles.js'

// `resolve` runs synchronously while the menu is being built, so neither
// collaborator can be a lazy import. Both are reachable without one: the live
// tune publishes its STATE as `window.__LIVE_TUNE__` (the object, not the
// store — it has no getState), and environmentFiles.js is already node-safe
// (it imports nothing but environmentAssetId.js, and reaches the API lazily).
let depsOverride = null

/** Test seam: `{ activeSplatUrl(), assetIdForUrl(url) }`. */
export function _setDepsForTests(d) { depsOverride = d ?? null }

const realDeps = {
  activeSplatUrl() {
    return (typeof window !== 'undefined' && window.__LIVE_TUNE__?.environment?.splatUrl) || null
  },
  assetIdForUrl(url) { return knownAssetId(url) },
}

function deps() { return depsOverride ?? realDeps }

/** The name and label the flyout has typed, or null when it has not been opened. */
let pendingName = null
let pendingLabel = ''

/** Set the name the next placement will use (called by the flyout editor). */
export function setPendingAnchorName(name) { pendingName = name == null ? null : String(name) }

/** The name the flyout currently holds. */
export function pendingAnchorName() { return pendingName }

/** Set the LABEL the next placement will carry -- the prose the LLM reads. */
export function setPendingAnchorLabel(label) { pendingLabel = label == null ? '' : String(label) }

/** The label the flyout currently holds. */
export function pendingAnchorLabel() { return pendingLabel }

/**
 * What a right-click can do about anchors right now.
 *
 * Every refusal is a REASON on a visible, disabled row — the repo's rule, and
 * here it matters more than usual: "no environment loaded" and "you clicked
 * somewhere the floor plane is not" are different problems with different
 * fixes, and a hidden row says neither.
 *
 * @returns {{assetId:string, name:string, point:number[]}|{error:string}}
 */
export function resolvePlaceAnchor(ctx) {
  if (!ctx?.point) return { error: 'no ground point under the cursor' }
  const d = deps()
  const url = d.activeSplatUrl()
  if (!url) return { error: 'no environment loaded' }
  const assetId = d.assetIdForUrl(url)
  // The pairing is stated when the environment is applied, so a null here means
  // the splat came from somewhere that never named a row (a ?env= URL to a bare
  // file). Saying that beats a save that 400s on an id we invented.
  if (!assetId) return { error: 'the live splat is not a saved environment asset' }

  const loaded = environmentAnchorStore.getState()
  if (loaded.assetId !== assetId) {
    // applyEnvironment adopts the row into the store, so this is a live splat
    // that got there some other way (a deep link, a blueprint node applying a
    // row this session never read). Re-applying is the fix, and saying so beats
    // retargeting the store silently — which is how one environment's anchors
    // end up saved onto another's row.
    return { error: 'the live environment is not the one loaded for anchoring — re-apply it from the content browser' }
  }
  const name = pendingName || suggestName('anchor', loaded.anchors)
  const labelErr = labelError(pendingLabel)
  if (labelErr) return { error: `label: ${labelErr}` }
  // The picked point is on the FLOOR plane, so py is floor height. An anchor
  // above the floor (a cup on a counter) is typed into the inspector -- the
  // raycast has no way to produce one.
  const anchor = anchorFromPoint(ctx.point.map(Number), pendingLabel)
  if (!anchor) return { error: 'the picked point is not a usable position' }
  return { assetId, name, anchor }
}

/**
 * Place the anchor and persist it. Exported so a spec (and the console) can
 * exercise the same path the menu row runs, rather than a parallel one.
 */
export async function runPlaceAnchor(ctx) {
  const r = resolvePlaceAnchor(ctx)
  if (r.error) throw new Error(r.error)
  if (!environmentAnchorStore.place(r.assetId, r.name, r.anchor)) {
    throw new Error(environmentAnchorStore.getState().error ?? 'could not place the anchor')
  }
  // Saved immediately — see the header. A failure leaves the anchor dirty and
  // the store's error set, which the inspector renders; it is not swallowed.
  await environmentAnchorStore.save(r.assetId)
  // One placement, one name: keep the flyout from re-using it and minting a
  // duplicate that silently MOVES the anchor the user just made. The label goes
  // with it -- it described THAT place.
  pendingName = null
  pendingLabel = ''
  return r
}

registerViewportCommand({
  id: 'placeAnchor',
  label: 'Place anchor here',
  order: 20, // below walk-here: walking is the common gesture, authoring is not
  resolve(ctx) {
    const r = resolvePlaceAnchor(ctx)
    if (r.error) return { enabled: false, reason: r.error }
    return { enabled: true, label: `Place anchor here (${r.name})` }
  },
  run: runPlaceAnchor,
})
